import { gsap } from "gsap";

export default class LogoLayer {
    constructor() {

        this.elm = document.querySelector('[data-logo-layer]');
        this.hasHost = false;

        if(!this.elm) {
            return;
        }

        this.logoSvg = this.elm.querySelector('[data-logo-svg]');
        gsap.set(this.logoSvg, {
            attr: {viewBox: "0 0 4000 4000"}
        });

        this.textN = this.elm.querySelectorAll('[data-text="n"]');
        this.textP = this.elm.querySelectorAll('[data-text="p"]');
        this.textO = this.elm.querySelectorAll('[data-text="o"]');
        this.textL = this.elm.querySelectorAll('[data-text="l"]');

        this.textCode = this.elm.querySelectorAll('[data-text="code"]');
        this.setCodeWidth(this.textCode[0]);

        this.blobN = this.elm.querySelector('[data-blob="n"]');
        this.blobP = this.elm.querySelector('[data-blob="p"]');
        this.blobO = this.elm.querySelector('[data-blob="o"]');
        this.blobL = this.elm.querySelector('[data-blob="l"]');

        this.blobCode = this.elm.querySelector('[data-blob="code"]');

        this.ratioX = 25;
        this.ratioY = 6;

        this.ratioN = -0.75;
        this.ratioP = -0.35;
        this.ratioO = 0.3;
        this.ratioL = 0.75;

        this.ratioCode = 1.3;

        gsap.defaults({
            overwrite: 'auto'
        });

    }

    resize(context) {
        if(context) {
            this.context = context;
        }

        if(!this.context) {
            console.error('missing context');
            return;
        }

        this.scale = this.context.conditions.isMobile ? 0.75 : 1;

        this.size = 30;
        this.sizeMedium = this.context.conditions.isMobile ? 66 : 125;
        this.sizeFull = ((window.innerWidth-30)/this.ratioX)*this.ratioY;

        this.x = 2000-(this.size/2);
        this.y = 2000-(this.size/2);

        this.offset = this.scale*((this.size/this.ratioY)*this.ratioX)/2;
        this.offsetMedium = (((this.sizeMedium/this.ratioY)*this.ratioX)/2);

        const dxs = {
            mobile: {
                P: this.scale*8,
            },
            desktop: {
                P: 8,
            },
        };

        let targetDxs = this.context.conditions.isMobile ? dxs.mobile : dxs.desktop;

        for(let key of Object.keys(targetDxs)){
            const dx = targetDxs[key];
            this[`text${key}`].forEach(elm => {
                elm.setAttribute('dx', dx);
            })
        }

        this.createTimeline();
    }

    attachToTop(hostClass) {
        this.isOnTop = true;
        this.elm.classList.add(hostClass);
    }

    detachFromTop() {
        this.isOnTop = false;
        this.elm.classList.remove('logo-layer--product');
        this.elm.classList.remove('logo-layer--top');
    }

    returnToCenter(duration, samePage) {
        return new Promise(resolve => {
            let targetTop = 0;
            if(this.prevTop&&samePage) {
                targetTop = this.prevTop;
            }
            this.elm.classList.remove('logo-layer--info');

            gsap.to(this.elm, {
                top: targetTop,
                duration: duration ? duration : 1.3,
                ease: 'power2.inOut',
                clearProps: 'top',
                onComplete: () => {
                    this.prevTop = null;
                    resolve();
                }
            });
        });
    }

    makeRoomForInfo(duration) {
        const svh = document.documentElement.clientHeight;
        gsap.to(this.elm, {
            top: this.context.conditions.isMobile ? ((svh/2)*-1)+44+11+'px' : 0+'px',
            duration: this.context.conditions.isMobile ? duration : 0,
            ease: 'power2.inOut',
            clearProps: 'top',
            onComplete: () => {
                this.elm.classList.add('logo-layer--info');
            }
        });
    }

    makeRoomForMenu(targetTop) {
        const y = gsap.getProperty(this.logoSvg, "y"); // Take offset from the About page into account..

        return new Promise(resolve => {
            gsap.to(this.elm, {
                top: targetTop+(y*-1)+'px',
                duration: 0.8,
                ease: 'power2.inOut',
                onComplete: () => {
                    resolve();
                }
            });
        });
    }


    createTimeline() {

        const time = this.master ? this.master.time() : 0;

        if(this.master) {
            this.master.kill();
        }

        this.master = gsap.timeline({
            id: 'master',
            paused: true
        });

        this.master.addLabel('init');
        this.master.add(this.initToPreloader());
        this.master.addLabel('preloader');
        this.master.add(this.preloaderToSmall());
        this.master.addLabel('small');
        this.master.add(this.smallToPreloader());
        this.master.addLabel('preloader2');
        this.master.add(this.preloaderToSmall());
        this.master.addLabel('small3');
        this.master.add(this.smallToMedium());
        this.master.addLabel('medium');
        this.master.add(this.mediumToSmall());
        this.master.addLabel('small4');
        this.master.add(this.smallToFull());
        this.master.addLabel('full');

        this.master.time(time);
    }

    goInitToPreloader() {
        return new Promise(resolve => {
            this.master.tweenFromTo('init', 'preloader', {
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goPreloaderToSmall() {
        return new Promise(resolve => {
            this.master.tweenFromTo('preloader', 'small', {
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goSmallToPreloader() {
        return new Promise(resolve => {
            this.master.tweenFromTo('small', 'preloader2', {
                ease: 'sine.out',
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goSmallToMedium() {
        return new Promise(resolve => {
            this.master.tweenFromTo('small3', 'medium', {
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goMediumToSmall() {
        return new Promise(resolve => {
            this.master.tweenFromTo('medium', 'small4', {
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goSmallToFull() {
        return new Promise(resolve => {
            this.master.tweenFromTo('small4', 'full', {
                onComplete: () => {
                    resolve();
                }
            });
        });
    }

    goFullToSmall() {
        return new Promise(resolve => {
            this.fullToSmall().then(() => {
                this.master.seek('small');
                resolve();
            });
        });
    }

    initToPreloader() {
        const duration = 0.3;
        const ease = 'power1.inOut';

        const tl = gsap.timeline({
            //
        });

        tl.set([
            this.blobN,
            this.blobP,
            this.blobO,
            this.blobL,
            this.blobCode
        ], {
            width: this.size,
            height: this.size,
            x: this.x,
            y: this.y,
            scale: 0,
        }, 0);

        tl.set([
            this.textN,
            this.textP,
            this.textO,
            this.textL
        ], {
            x: this.x,
            y: this.y,
            scale: 0
        }, 0);

        tl.set(this.textCode, {
            x: this.x,
            y: this.y,
            opacity: 0
        }, 0);

        // Blobs
        tl.to([
            this.blobN,
            this.blobP,
            this.blobO,
            this.blobL
        ], {
            scale: this.scale,

            duration: duration,
            ease: ease,
        });

        tl.set([
            this.blobP,
            this.blobO,
            this.blobL
        ], {
            opacity: 1
        });

        return tl;
    }

    preloaderToSmall() {
        const duration = 0.3;
        const ease = 'power1.inOut';

        const tl = gsap.timeline({
            //
        });

        tl.addLabel("start");

        tl.set([
            this.blobN,
            this.textN,
            this.blobP,
            this.textP
        ], {
            x: this.x,
        }, "start");

        tl.set([
            this.textN,
            this.textP,
            this.textO,
            this.textL,
        ], {
            opacity: 0,
            scale: 0,
        }, "start");

        tl.set(this.blobO, {
            width: (this.size/2)*3,
            opacity: 0,
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105)-(this.offset*0.325),
        }, "start");
        tl.set(this.textO, {
            opacity: 0,
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105),
        }, "start");
        tl.set([this.blobL, this.textL], {
            opacity: 0,
            x: (this.x+(this.offset*this.ratioL))-(this.offset*0.105),
        }, "start");

        tl.set([this.blobCode], {
            x: (this.x+(this.offset*this.ratioCode))-(this.offset*0.105),
        }, "start");

        tl.to([this.blobN, this.textN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105)+(this.offset*0.325)+(this.offset*0.12),

            duration: duration,
            ease: ease,
        }, 'start');
        tl.to(this.textN, {
            scale: this.scale,
            opacity: 1,

            duration: duration*1.5,
            ease: ease,
        }, 'start');
        tl.to(this.blobP, {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105)+(this.offset*0.325)+(this.offset*0.12),

            duration: duration,
            ease: ease,
        }, 'start');


        tl.addLabel("step1", ">");
        tl.to([this.blobN, this.textN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105)+(this.offset*0.325),

            duration: duration,
            ease: ease,
        }, 'step1');
        tl.to([this.blobP, this.textP], {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105)+(this.offset*0.325),
            width: (this.size/2)*3,

            duration: duration,
            ease: ease,
        }, 'step1');
        tl.to(this.textP, {
            scale: this.scale,
            opacity: 1,

            duration: duration*1.5,
            ease: ease,
        }, 'step1');


        tl.addLabel("step2", ">");
        tl.to([this.blobN, this.textN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105),

            duration: duration,
            ease: ease,
        }, 'step2');
        tl.to([this.blobP, this.textP], {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105),

            duration: duration,
            ease: ease,
        }, 'step2');

        tl.set(this.blobO, { // Show blobO
            opacity: 1,
        }, 'step2');
        tl.to(this.blobO, {
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105),

            duration: duration,
            ease: ease,

        }, 'step2');
        tl.to(this.textO, {
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105)+this.size/8,

            duration: duration,
            ease: ease,
        }, 'step2');
        tl.to(this.textO, {
            scale: this.scale,
            opacity: 1,

            duration: duration*1.5,
            ease: ease,
        }, 'step2');


        tl.addLabel("step3");
        tl.to(this.blobO, {
            width: this.size,

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.to([this.blobN, this.textN], {
            x: this.x+(this.offset*this.ratioN),

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.to([this.blobP, this.textP], {
            x: this.x+(this.offset*this.ratioP),

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.to([this.blobO, this.textO], {
            x: this.x+(this.offset*this.ratioO),

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.set(this.blobL, { // Show blobL
            opacity: 1,
        }, 'step3');
        tl.to([this.blobL, this.textL], {
            x: this.x+(this.offset*this.ratioL),

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.to(this.textL, {
            scale: this.scale,
            opacity: 1,

            duration: duration*1.5,
            ease: ease,
        }, 'step3');

        return tl;
    }

    smallToPreloader() {
        const duration = 0.3;
        const ease = 'sine.inOut';

        const tl = gsap.timeline({
            //
        });

        tl.addLabel("start");

        tl.set([
            this.blobL,
            this.blobO
        ], {
            opacity: 1
        }, 'start');

        tl.to([this.textL, this.textO], {
            scale: 0,
            opacity: 0,

            duration: duration*1.5,
            ease: ease,
        }, 'start');

        tl.to(this.blobO, {
            width: (this.size/2)*3,

            duration: duration,
            ease: ease,
        }, 'start');
        tl.to([this.textN, this.blobN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105),

            duration: duration,
            ease: ease,
        }, 'start');
        tl.to([this.textP, this.blobP], {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105),

            duration: duration,
            ease: ease,
        }, 'start');
        tl.to([this.textO, this.blobO], {
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105),

            duration: duration,
            ease: ease,
        }, 'start');


        tl.to([this.textL, this.blobL], {
            x: (this.x+(this.offset*this.ratioL))-(this.offset*0.105),

            duration: duration,
            ease: ease,

        }, 'start');
        tl.set(this.blobL, { // Hide blobL
            opacity: 0,
        });

        tl.addLabel("step2", ">");
        tl.to([this.textN, this.blobN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105)+(this.offset*0.325),

            duration: duration,
            ease: ease,
        }, 'step2');
        tl.to([this.textP, this.blobP], {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105)+(this.offset*0.325),

            duration: duration,
            ease: ease,
        }, 'step2');
        tl.to(this.textP, {
            scale: 0,
            opacity: 0,

            duration: duration*1.5,
            ease: ease,
        }, 'step2');

        tl.to([this.textO, this.blobO], {
            x: this.x+(this.offset*this.ratioO)+(this.offset*0.105)-(this.offset*0.325),

            duration: duration,
            ease: ease,
        }, 'step2');
        tl.set(this.blobO, { // Hide blobO
            opacity: 0,
        });

        tl.addLabel("step3", ">");
        tl.to([this.textN, this.blobN], {
            x: this.x+(this.offset*this.ratioN)+(this.offset*0.105)+(this.offset*0.325)+(this.offset*0.12),

            duration: duration,
            ease: ease,
        }, 'step3');
        tl.to(this.textN, {
            scale: 0,
            opacity: 0,

            duration: duration*1.5,
            ease: ease,
        }, 'step3');
        tl.to(this.blobP, {
            x: this.x+(this.offset*this.ratioP)+(this.offset*0.105)+(this.offset*0.325)+(this.offset*0.12),
            width: this.size,

            duration: duration,
            ease: ease,
        }, 'step3');


        tl.addLabel("step4", ">");
        tl.to([this.blobN, this.blobP], {
            x: this.x,

            duration: duration,
            ease: ease,
        }, 'step4');

        return tl;
    }

    smallToMedium() {
        const duration = 1;

        const tl = gsap.timeline({
            //
        });

        tl.to(
			this.logoSvg,
			{
				opacity: 0.6,
				y: 0,

				duration: duration,
				ease: 'power2.inOut',
			},
			0
		);

        tl.addLabel('N', 0);
        tl.to(this.blobN, {
            scale: this.sizeMedium/this.size,

            duration: duration*0.75,
            ease: "power2.inOut",
        }, 'N');
        tl.to(this.blobN, {
            x: this.x+(this.offsetMedium*this.ratioN),

            duration: duration,
            ease: "power2.inOut",
        }, 'N');
        tl.to(this.textN, {
            x: this.x+(this.offsetMedium*this.ratioN)+(this.scale*1),

            duration: duration,
            ease: "power2.inOut",
        }, 'N');

        tl.addLabel('P', 0);
        tl.to(this.blobP, {
            scale: this.sizeMedium/this.size,
            x: this.x+(this.offsetMedium*this.ratioP),

            duration: duration,
            ease: "power2.inOut"
        }, 'P');
        tl.to(this.textP, {
            x: this.x+(this.offsetMedium*this.ratioP)+((this.sizeMedium/3)/2)+(this.scale*5),

            duration: duration,
            ease: "power2.inOut"
        }, 'P');

        tl.addLabel('O', duration/5);
        tl.to(this.blobO, {
            scale: this.sizeMedium/this.size,

            duration: duration,
            ease: "power3.inOut"
        }, 'O');
        tl.to(this.blobO, {
            x: this.x+(this.offsetMedium*this.ratioO),

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'O');
        tl.to(this.textO, {
            x: this.x+(this.offsetMedium*this.ratioO)+(this.scale*3),

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'O');

        tl.addLabel('L', duration/5);
        tl.to(this.blobL, {
            scale: this.sizeMedium/this.size,

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'L');
        tl.to(this.blobL, {
            x: this.x+(this.offsetMedium*this.ratioL),

            duration: duration,
            ease: "power3.inOut"
        }, 'L');
        tl.to(this.textL, {
            x: this.x+(this.offsetMedium*this.ratioL)+(this.scale*4),

            duration: duration,
            ease: "power3.inOut"
        }, 'L');

        return tl;
    }


    mediumToSmall() {
        const duration = 1.5;

        const tl = gsap.timeline({
            //
        });

        tl.to(this.logoSvg, {
            opacity: 0.8,
            y: 0,

            duration: duration,
            ease: "none",
        }, 0);

        tl.addLabel('L', 0);
        tl.to(this.blobL, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioL),

            duration: duration,
            ease: "back.out(1.4)"
        }, 'L');
        tl.to(this.textL, {
            x: this.x+(this.offset*this.ratioL),

            duration: duration,
            ease: "back.out(1.4)"
        }, 'L');

        tl.addLabel('O', duration/10);
        tl.to(this.blobO, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioO),

            duration: duration,
            ease: "back.out(1)"
        }, 'O');
        tl.to(this.textO, {
            x: this.x+(this.offset*this.ratioO),

            duration: duration,
            ease: "back.out(1)"
        }, 'O');

        tl.addLabel('P', duration/10);
        tl.to(this.blobP, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioP),

            duration: duration,
            ease: "back.inOut(1)"
        }, 'P');
        tl.to(this.textP, {
            x: this.x+(this.offset*this.ratioP),

            duration: duration,
            ease: "back.inOut(1)"
        }, 'P');

        tl.addLabel('N', duration/10);
        tl.to(this.blobN, {
            scale: this.scale,

            duration: duration,
            ease: "back.inOut(1.7)",

        }, 'N');
        tl.to(this.blobN, {
            x: this.x+(this.offset*this.ratioN),

            duration: duration*0.75,
            ease: "back.inOut(1.7)",
        }, 'N');
        tl.to(this.textN, {
            x: this.x+(this.offset*this.ratioN),

            duration: duration*0.75,
            ease: "back.inOut(1.7)",
        }, 'N');

        return tl;
    }

    smallToFull() {
        const ease = 'power2.inOut';
        const duration = 1.6;
        const offset = (((this.sizeFull/this.ratioY)*this.ratioX)/2);

        const tl = gsap.timeline({
            onComplete: () => {
                document.dispatchEvent(new CustomEvent('enable-scroll'));

                this.fullScrollAnimation();
            }
        });


        tl.to(this.logoSvg, {
            opacity: 0.8,
            y: () => {
                return this.context.conditions.isMobile ? 0 : (window.innerHeight/2 * -1) + this.sizeFull/2 + this.size
            },

            duration: duration,
            ease: ease,
        }, 0);

        tl.addLabel('N', 0);
        tl.to(this.blobN, {
            scale: this.sizeFull/this.size,

            duration: duration*0.75,
            ease: "power2.inOut",
        }, 'N');
        tl.to(this.blobN, {
            x: this.x+(offset*this.ratioN),

            duration: duration,
            ease: "power2.inOut",
        }, 'N');
        tl.to(this.textN, {
            x: this.x+(offset*this.ratioN),

            duration: duration,
            ease: "power2.inOut",
        }, 'N');

        tl.addLabel('P', 0);
        tl.to(this.blobP, {
            scale: this.sizeFull/this.size,
            x: this.x+(offset*this.ratioP),

            duration: duration,
            ease: "power2.inOut"
        }, 'P');
        tl.to(this.textP, {
            x: this.x+(offset*this.ratioP)+((this.sizeFull/3)/2)+((this.sizeFull/this.size)*3),

            duration: duration,
            ease: "power2.inOut"
        }, 'P');

        tl.addLabel('O', duration/5);
        tl.to(this.blobO, {
            scale: this.sizeFull/this.size,

            duration: duration,
            ease: "power3.inOut"
        }, 'O');
        tl.to(this.blobO, {
            x: this.x+(offset*this.ratioO),

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'O');
        tl.to(this.textO, {
            x: this.x+(offset*this.ratioO),

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'O');

        tl.addLabel('L', duration/5);
        tl.to(this.blobL, {
            scale: this.sizeFull/this.size,

            duration: duration*0.9,
            ease: "power3.inOut"
        }, 'L');
        tl.to(this.blobL, {
            x: this.x+(offset*this.ratioL),

            duration: duration,
            ease: "power3.inOut"
        }, 'L');
        tl.to(this.textL, {
            x: this.x+(offset*this.ratioL),

            duration: duration,
            ease: "power3.inOut"
        }, 'L');

        return tl;
    }

    fullToSmall() {
        return new Promise(resolve => {
            const duration = 2;

            const tl = gsap.timeline({
                onStart: () => {
                    this.killFullScrollAnimation();
                },
                onComplete: () => {
                    tl.kill();
                    resolve();
                }
            });

            tl.to(this.logoSvg, {
                y: 0,

                duration: duration,
                ease: 'power4.inOut',
            }, 0);

            // Blobs
            tl.addLabel('L', duration/20);
            tl.to(this.blobL, {
                scale: this.scale,
                x: this.x+(this.offset*this.ratioL),

                duration: duration*0.8,
                ease: "power3.inOut",
            }, 'L');
            tl.to(this.textL, {
                x: this.x+(this.offset*this.ratioL),

                duration: duration*0.8,
                ease: "power3.inOut",
            }, 'L');

            tl.addLabel('O', 0);
            tl.to(this.blobO, {
                scale: this.scale,
                x: this.x+(this.offset*this.ratioO),

                duration: duration,
                ease: "expo.inOut",
            }, 'O');
            tl.to(this.textO, {
                x: this.x+(this.offset*this.ratioO),

                duration: duration,
                ease: "expo.inOut",
            }, 'O');

            tl.addLabel('P', 0);
            tl.to(this.blobP, {
                scale: this.scale,
                x: this.x+(this.offset*this.ratioP),

                duration: duration,
                ease: "expo.inOut",
            }, 'P');
            tl.to(this.textP, {
                x: this.x+(this.offset*this.ratioP),

                duration: duration,
                ease: "expo.inOut",
            }, 'P');

            tl.addLabel('N', duration/20);
            tl.to(this.blobN, {
                scale: this.scale,
                x: this.x+(this.offset*this.ratioN),

                duration: duration*0.8,
                ease: "power3.inOut",
            }, 'N');
            tl.to(this.textN, {
                x: this.x+(this.offset*this.ratioN),

                duration: duration*0.8,
                ease: "power3.inOut",
            }, 'N');

        });
    }

    fullScrollAnimation() {

        const triggerElm = document.querySelector('[data-about-scrolltrigger-elm]');
        this.killFullScrollAnimation();

        this.fullScrollAnimationTimeline = gsap.timeline({
            paused: true,
            overwrite: true,
            scrollTrigger: {
                trigger: triggerElm,
                start: 'top top',
                end: 'bottom top',
                scrub: 0.8,
            }
        });

        const duration = 2.4;
        const ease = "none";

        this.fullScrollAnimationTimeline.to(this.logoSvg, {
            opacity: 0.8,
            y: () => {
                return this.context.conditions.isMobile ? (window.innerHeight/2 * -1) + (this.size*this.scale)/2 + (this.size*this.scale) : (window.innerHeight/2 * -1) + this.size/2 + this.size
            },

            duration: duration,
            ease: ease,
        }, 0);

        // Blobs
        this.fullScrollAnimationTimeline.to(this.blobN, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioN),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.blobP, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioP),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.blobO, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioO),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.blobL, {
            scale: this.scale,
            x: this.x+(this.offset*this.ratioL),

            duration: duration,
            ease: ease,
        }, 0);

        // Text
        this.fullScrollAnimationTimeline.to(this.textN, {
            x: this.x+(this.offset*this.ratioN),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.textP, {
            x: this.x+(this.offset*this.ratioP),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.textO, {
            x: this.x+(this.offset*this.ratioO),

            duration: duration,
            ease: ease,
        }, 0);
        this.fullScrollAnimationTimeline.to(this.textL, {
            x: this.x+(this.offset*this.ratioL),

            duration: duration,
            ease: ease,
        }, 0);

    }

    killFullScrollAnimation() {
        if(this.fullScrollAnimationTimeline) {
            this.fullScrollAnimationTimeline.kill();
        }
    }

    setCodeWidth(elm) {
        const codeTextLength = elm.getComputedTextLength();
        let codeRectWidth = elm.getBoundingClientRect().width;
        if(codeRectWidth<codeTextLength) {
            codeRectWidth = (codeRectWidth/0.75)*1;
        }
        this.codeWidth = Math.max(codeTextLength, codeRectWidth);
    }

    smallToCode(code) {
        return new Promise(resolve => {
            const duration = 0.4;
            const ease = 'power1.inOut';

            gsap.set([
                this.blobCode,
                this.textCode
            ], {
                x: this.x+(this.offset*this.ratioL),
                scale: this.scale,
                onComplete: () => {

                    this.textCode.forEach(elm => {
                        elm.innerHTML = code;
                    });

                    this.setCodeWidth(this.textCode[0]);

                    gsap.set(this.blobCode, {
                        width: this.size,
                        opacity: 1
                    });

                    gsap.set(this.textCode, {
                        opacity: 0,
                        scale: this.scale,
                        onComplete: () => {
                            gsap.set(this.textCode, {
                                x: this.x+((this.offset*this.ratioCode)+((this.codeWidth)/2)*this.scale),
                            });
                        }
                    });

                    const tl = gsap.timeline({
                        onComplete: () => {
                            resolve();
                        },
                    });

                    tl.to(this.blobCode, {
                        x: this.x+(this.offset*this.ratioCode),
                        duration: duration,
                        ease: 'power1.in'
                    });

                    tl.to(this.blobCode, {
                        width: this.size+this.codeWidth,
                        duration: duration,
                        ease: 'power1.out'
                    });

                    tl.to(this.textCode, {
                        opacity: 1,
                        duration: duration,
                        ease: ease
                    }, duration+duration/2);

                    tl.to(this.logoSvg, {
                        x: (((((this.offset*2)+(this.size*0.15)+this.size+this.codeWidth)/2)-this.offset)*-1 ) * this.scale,
                        duration: duration*2,
                        ease: ease
                    }, 0);
                }
            });

        });
    }

    renderCode(code) {

        gsap.set(this.textCode, {
            scale: this.scale,
            overwrite: true,
            onComplete: () => {
                this.textCode.forEach(elm => {
                    elm.innerHTML = code;
                });
                this.setCodeWidth(this.textCode[0]);

                gsap.set(this.textCode, {
                    opacity: 1,
                    x: this.x+((this.offset*this.ratioCode)+((this.codeWidth)/2)*this.scale),
                    overwrite: true
                });

                gsap.set(this.blobCode, {
                    width: this.size+this.codeWidth,
                    opacity: 1,
                    scale: this.scale,
                    x: this.x+(this.offset*this.ratioCode),
                    overwrite: true
                });

                gsap.set(this.logoSvg, {
                    x: (((((this.offset*2)+(this.size*0.15)+this.size+this.codeWidth)/2)-this.offset)*-1 ) * this.scale,
                    overwrite: true
                });
            }
        });

    }

    updateCode(code) {

        return new Promise(resolve => {

            let duration = 0.4;
            const ease = 'power1.inOut';

            gsap.to(this.textCode, {
                opacity: 0,
                duration: 0,
                ease: ease,
                onComplete: () => {

                    this.textCode.forEach(elm => {
                        elm.innerHTML = code;
                    });
                    const prevCodeWidth = this.codeWidth;

                    this.setCodeWidth(this.textCode[0]);

                    if(prevCodeWidth===this.codeWidth) {
                        duration = 0;
                    }

                    const tl = gsap.timeline({
                        onComplete: () => {
                            resolve();
                        },
                    });

                    tl.to(this.blobCode, {
                        width: this.size+this.codeWidth,
                        duration: duration,
                        ease: ease
                    }, 0);

                    tl.to(this.logoSvg, {
                        x: (((((this.offset*2)+(this.size*0.15)+this.size+this.codeWidth)/2)-this.offset)*-1 ) * this.scale,
                        duration: duration,
                        ease: ease
                    }, 0);

                    tl.to(this.textCode, {
                        x: this.x+((this.offset*this.ratioCode)+((this.codeWidth)/2)*this.scale),
                        duration: duration,
                        ease: ease
                    }, 0);

                    tl.to(this.textCode, {
                        opacity: 1,
                        duration: duration,
                        ease: ease
                    }, 0);

                }
            });

        });
    }

    codeToSmall() {
        return new Promise(resolve => {
            const duration = 0.6;
            const ease = 'power1.out';

            const tl = gsap.timeline({
                onComplete: () => {

                    gsap.set(this.blobCode, {
                        opacity: 0,
                        width: 0,
                    });

                    this.textCode.forEach(elm => {
                        elm.innerHTML = '';
                    });

                    resolve();
                }
            });

            tl.to(this.textCode, {
                opacity: 0,
                duration: duration/3,
                ease: ease,
            }, 0);

            tl.to(this.blobCode, {
                width: this.size,
                x: this.x+(this.offset*this.ratioL),
                duration: duration,
                ease: ease,
            }, duration/3);

            tl.to(this.logoSvg, {
                x: 0,
                duration: duration,
                ease: ease,
            }, duration/3);

        });
    }

}
